import { ArrowLeft, Pencil } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';

import { CarrierPocketbook } from '@/components/carrier-directory/CarrierPocketbook';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api';
import {
  useAddCoverageContactMutation,
  useGetCoverageContactsCountQuery,
  useGetCoverageQuery,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import {
  usePropertiesCreateMutation,
  usePropertiesUpdateMutation,
  usePropertiesWithOwnersQuery,
} from '@/services/api/subjectsService';
import { Task } from '@/services/types/client-intake-types';
import {
  CoverageCategory,
  CoverageSchema,
  UpdateCoverageSchema,
} from '@/services/types/coverage-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import { BasicInfoTab } from './forms/BasicInfoTab';
import { BenefitsTab } from './forms/BenefitsTab';
import { ClaimsInfoTab } from './forms/ClaimsInfoTab';
import { CoverageFormTabs } from './forms/CoverageFormTabs';
import { PolicyDetailsTab } from './forms/PolicyDetailsTab';
import {
  formatPropertyContactsForApi,
  mapPropertyContactsToFormData,
  PropertyContactFormData,
  PropertyContactsTab,
} from './forms/PropertyContactsTab';
import {
  isMailingAddressComplete,
  mailingAddressHasAnyValue,
} from './propertyContactAddress';
import {
  CoverageFormData,
  createCoverageFormDefaults,
  mapCoverageToFormValues,
} from './forms/types';

interface EditCoverageLocationState {
  coverage?: CoverageSchema;
}

const COVERAGE_TABS = ['basic', 'policy', 'benefits', 'claims'] as const;
const PREMISES_COVERAGE_TABS = [
  'basic',
  'contacts',
  'policy',
  'benefits',
  'claims',
] as const;
type CoverageTab =
  | (typeof COVERAGE_TABS)[number]
  | (typeof PREMISES_COVERAGE_TABS)[number];
const isValidCoverageTab = (
  value: string | null,
  isPremises: boolean,
): value is CoverageTab => {
  const tabs: readonly string[] = isPremises
    ? PREMISES_COVERAGE_TABS
    : COVERAGE_TABS;
  return Boolean(value && tabs.includes(value));
};

const resolveListUpdate = <T,>(
  currentValues: T[],
  initialValues: T[] | null | undefined,
): T[] | null => {
  if (currentValues.length > 0) {
    return currentValues;
  }
  if (Array.isArray(initialValues) && initialValues.length > 0) {
    return [];
  }
  return null;
};

export const EditCoveragePage = () => {
  const { matter_id, coverage_id } = useParams<{
    matter_id: string;
    coverage_id: string;
  }>();
  const location = useLocation();
  const coverageFromState = (
    location.state as EditCoverageLocationState | null | undefined
  )?.coverage;
  const navigate = useNavigate();
  const [updateCoverage, { isLoading: isUpdating }] =
    useUpdateCoverageMutation();

  const matterIdParam = matter_id || '';
  const coverageIdParam = coverage_id || '';
  const { data: coverage, isLoading: isLoadingCoverage } = useGetCoverageQuery(
    { matterId: matterIdParam, coverageId: coverageIdParam },
    {
      skip: !matter_id || !coverage_id,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  // Determine if this is a premises coverage
  const isPremisesCoverage = coverage?.category === CoverageCategory.PROPERTY;
  const availableTabs = isPremisesCoverage
    ? PREMISES_COVERAGE_TABS
    : COVERAGE_TABS;

  const [activeTab, setActiveTabState] = useState<CoverageTab>(
    isValidCoverageTab(tabParam, isPremisesCoverage)
      ? (tabParam as CoverageTab)
      : 'basic',
  );

  // Property contacts state for premises coverages
  const [propertyContacts, setPropertyContacts] = useState<
    PropertyContactFormData[]
  >(() => mapPropertyContactsToFormData(coverage?.property_contacts));

  // Fetch property data for premises coverages (for property_name)
  const { data: propertyWithOwners } = usePropertiesWithOwnersQuery(
    {
      id: coverage?.property_id ?? '',
      matterId: matterIdParam,
    },
    {
      skip:
        !coverage?.property_id ||
        coverage?.category !== CoverageCategory.PROPERTY,
    },
  );
  const [createProperty] = usePropertiesCreateMutation();
  const [updateProperty] = usePropertiesUpdateMutation();
  const [propertyName, setPropertyName] = useState<string>('');
  const hasUserEditedPropertyName = useRef(false);

  // Initialize property name when property data loads (only if user hasn't edited)
  useEffect(() => {
    if (
      propertyWithOwners?.property_name !== undefined &&
      !hasUserEditedPropertyName.current
    ) {
      setPropertyName(propertyWithOwners.property_name ?? '');
    }
  }, [propertyWithOwners?.property_name]);

  useEffect(() => {
    const nextParam = searchParams.get('tab');
    if (
      isValidCoverageTab(nextParam, isPremisesCoverage) &&
      nextParam !== activeTab
    ) {
      setActiveTabState(nextParam);
      return;
    }
    if (!nextParam) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, activeTab, setSearchParams, isPremisesCoverage]);

  // Initialize property contacts when coverage data loads
  useEffect(() => {
    if (coverage?.property_contacts) {
      setPropertyContacts(
        mapPropertyContactsToFormData(coverage.property_contacts),
      );
    }
  }, [coverage]);

  const handleTabChange = (tab: CoverageTab) => {
    setActiveTabState(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // Preferred contacts hooks
  const { data: preferredCountRaw = 0, refetch: refetchPreferred } =
    useGetCoverageContactsCountQuery(
      { matterId: matterIdParam, coverageId: coverageIdParam },
      { skip: !matter_id || !coverage_id },
    );
  const [addPreferred] = useAddCoverageContactMutation();

  // Derive count regardless of response shape (ids vs full handles)
  const preferredCount = Number(preferredCountRaw || 0);

  // Task hooks for "Mark Review Complete" functionality
  const { data: tasksResponse } = useGetTasksQuery({
    matter: matterIdParam,
    limit: 200,
  });
  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();
  const [updateTask, { isLoading: isCompletingTask }] = useUpdateTaskMutation();

  // Find the limits review task for this coverage (Dec Page for 1P, Get Liability Limits for 3P)
  const limitsReviewTask = useMemo(() => {
    if (!coverage || !tasksResponse?.results) return undefined;
    const tasks = tasksResponse.results as Task[];
    const coverageTasks = tasks.filter(
      (t) =>
        t.coverage_id === coverage.id ||
        t.coverage?.id === coverage.id,
    );
    const targetType =
      coverage.category === CoverageCategory.FIRST_PARTY
        ? CanonicalTaskType.DECLARATION_PAGE
        : CanonicalTaskType.GET_LIABILITY_LIMITS;
    return coverageTasks.find((t) => t.type?.canonical_type === targetType);
  }, [coverage, tasksResponse?.results]);

  const isLimitsReviewTaskCompleted =
    limitsReviewTask?.status?.canonical_status ===
    CanonicalTaskStatus.COMPLETED;

  const handleMarkReviewComplete = async () => {
    if (!limitsReviewTask || !matter_id) return;

    const completedStatus = taskStatuses.find(
      (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
    );
    if (!completedStatus) {
      toast.error('Could not find completed status');
      return;
    }

    try {
      await updateTask({
        matterId: matter_id,
        taskId: limitsReviewTask.id,
        task: {
          status_id: completedStatus.id,
          completed_at: new Date().toISOString(),
        },
      }).unwrap();
      toast.success('Review marked as complete');
    } catch (error) {
      toast.error('Failed to mark review as complete');
    }
  };

  const { register, handleSubmit, reset, setValue, watch, control, getValues } =
    useForm<CoverageFormData>({
      defaultValues: createCoverageFormDefaults(),
    });
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [removingClaimIndex, setRemovingClaimIndex] = useState<number | null>(
    null,
  );
  const benefitsFlushRef = useRef<(() => void) | null>(null);

  const {
    fields: insuredFields,
    append: appendInsured,
    remove: removeInsured,
  } = useFieldArray({
    control,
    name: 'named_insured',
  });

  const {
    fields: policyIdFields,
    append: appendPolicyId,
    remove: removePolicyId,
  } = useFieldArray({
    control,
    name: 'policy_identifiers',
  });

  const {
    fields: policyDetailFields,
    append: appendPolicyDetail,
    remove: removePolicyDetail,
  } = useFieldArray({
    control,
    name: 'policy_details',
  });

  const {
    fields: benefitFields,
    append: appendBenefit,
    remove: removeBenefit,
    update: updateBenefit,
  } = useFieldArray({
    control,
    name: 'benefits',
  });

  const {
    fields: vehicleFields,
    append: appendVehicle,
    remove: removeVehicle,
  } = useFieldArray({
    control,
    name: 'vehicles',
  });

  const {
    fields: claimFields,
    append: appendClaim,
    remove: removeClaim,
    insert: insertClaim,
  } = useFieldArray({
    control,
    name: 'claims',
  });

  const {
    fields: adjusterFields,
    append: appendAdjuster,
    remove: removeAdjuster,
  } = useFieldArray({
    control,
    name: 'adjusters',
  });

  useEffect(() => {
    const hydratedCoverage = coverage ?? coverageFromState;
    if (!hydratedCoverage) {
      // eslint-disable-next-line no-console
      console.log(
        'EditCoveragePage: no coverage data available to prefill yet',
        {
          hasApiData: Boolean(coverage),
          hasStateData: Boolean(coverageFromState),
        },
      );
      return;
    }

    const mappedValues = mapCoverageToFormValues(hydratedCoverage);

    reset(mappedValues, {
      keepDirty: false,
      keepErrors: false,
      keepValues: false,
    });

    setValue('category', mappedValues.category, { shouldDirty: false });
    setValue('policy_type', mappedValues.policy_type, { shouldDirty: false });
    setValue('liability_status', mappedValues.liability_status, {
      shouldDirty: false,
    });
    setValue('denial_reason', mappedValues.denial_reason, {
      shouldDirty: false,
    });
    setValue('carrier.name', mappedValues.carrier.name, { shouldDirty: false });
    setValue('carrier.handles', mappedValues.carrier.handles, {
      shouldDirty: false,
    });
    setValue('carrier_v2_id', mappedValues.carrier_v2_id, {
      shouldDirty: false,
    });
    setValue('carrier_division_id', mappedValues.carrier_division_id, {
      shouldDirty: false,
    });
    setValue('subrogator.handles', mappedValues.subrogator.handles, {
      shouldDirty: false,
    });
    setValue('subrogator.sources', mappedValues.subrogator.sources, {
      shouldDirty: false,
    });

    setIsPrefilled(true);
  }, [coverage, coverageFromState, reset, setValue]);

  const handleRemoveClaim = async (index: number) => {
    if (!matter_id || !coverage_id || !coverage) {
      removeClaim(index);
      return;
    }

    const currentClaims = getValues('claims') || [];
    const claimToRestore = currentClaims[index];

    setRemovingClaimIndex(index);
    removeClaim(index);

    const nextClaims = getValues('claims') || [];

    try {
      await updateCoverage({
        matterId: matter_id,
        coverageId: coverage_id,
        coverage: {
          id: coverage.id,
          status: coverage.status,
          claims: resolveListUpdate(nextClaims, coverage.claims),
        },
      }).unwrap();
      toast.success('Claim removed');
    } catch (error) {
      if (claimToRestore) {
        insertClaim(index, claimToRestore);
      }
      toast.error('Failed to remove claim');
    } finally {
      setRemovingClaimIndex(null);
    }
  };


  const onSubmit = async (data: CoverageFormData) => {
    if (!matter_id || !coverage_id || !coverage) return;

    try {
      const normalizedNamedInsured = data.named_insured
        .map((item) => item.name?.trim() ?? '')
        .filter((name): name is string => Boolean(name));

      const invalidPropertyContact = isPremisesCoverage
        ? propertyContacts.find(
            (contact) =>
              contact.name?.trim() &&
              mailingAddressHasAnyValue(contact.mailing_address) &&
              !isMailingAddressComplete(contact.mailing_address),
          )
        : null;
      if (invalidPropertyContact) {
        toast.error(
          `Complete the mailing address for ${invalidPropertyContact.name || 'this contact'}. Street, city, state, and ZIP are required.`,
        );
        return;
      }

      // Format property contacts for premises coverages
      const formattedPropertyContacts = isPremisesCoverage
        ? formatPropertyContactsForApi(propertyContacts)
        : undefined;

      const updateData: UpdateCoverageSchema = {
        id: coverage.id,
        status: coverage.status,
        liability_status: data.liability_status,
        denial_reason:
          data.liability_status === 'REJECTED' ||
          data.liability_status === 'PARTIALLY_ACCEPTED'
            ? data.denial_reason
            : null,
        category: data.category,
        policy_type: data.policy_type,
        carrier: data.carrier.name
          ? {
              name: data.carrier.name,
              handles:
                data.carrier.handles && data.carrier.handles.length > 0
                  ? data.carrier.handles
                  : null,
              address: data.carrier.address || null,
            }
          : { name: null, handles: null, address: null },
        policy_identifiers: resolveListUpdate(
          data.policy_identifiers,
          coverage.policy_identifiers,
        ),
        named_insured: resolveListUpdate(
          normalizedNamedInsured,
          coverage.named_insured,
        ),
        benefits: resolveListUpdate(data.benefits, coverage.benefits),
        adjusters: resolveListUpdate(data.adjusters, coverage.adjusters),
        claims: resolveListUpdate(data.claims, coverage.claims),
        vehicles: resolveListUpdate(data.vehicles, coverage.vehicles),
        policy_details: resolveListUpdate(
          data.policy_details,
          coverage.policy_details,
        ),
        carrier_v2_id: data.carrier.name ? data.carrier_v2_id : null,
        carrier_division_id: data.carrier.name
          ? data.carrier_division_id
          : null,
        subrogator: data.subrogator.name
          ? {
              name: data.subrogator.name,
              handles:
                data.subrogator.handles && data.subrogator.handles.length > 0
                  ? data.subrogator.handles
                  : null,
              sources:
                data.subrogator.sources && data.subrogator.sources.length > 0
                  ? data.subrogator.sources
                  : null,
            }
          : null,
        // Include property contacts for premises coverages
        ...(formattedPropertyContacts !== undefined && {
          property_contacts: formattedPropertyContacts,
        }),
      };

      await updateCoverage({
        matterId: matter_id,
        coverageId: coverage_id,
        coverage: updateData,
      }).unwrap();

      // Also update property_name if this is a premises coverage
      if (isPremisesCoverage && (propertyName || coverage.property_id)) {
        try {
          if (coverage.property_id) {
            // Update existing property name
            if (propertyName !== (propertyWithOwners?.property_name ?? '')) {
              await updateProperty({
                id: coverage.property_id,
                matterId: matter_id,
                patchedUpdateProperty: {
                  id: coverage.property_id,
                  property_name: propertyName || null,
                },
              }).unwrap();
            }
          } else {
            // No property linked yet — create one and link it to the coverage
            const newProperty = await createProperty({
              matterId: matter_id,
              createProperty: {
                matter_id: matter_id,
                property_name: propertyName,
                real_property_type: 'commercial',
              },
            }).unwrap();
            await updateCoverage({
              matterId: matter_id,
              coverageId: coverage_id,
              coverage: {
                id: coverage.id,
                status: coverage.status,
                property_id: newProperty.id,
              },
            }).unwrap();
          }
        } catch {
          toast.error('Coverage saved, but failed to update property name');
          navigate(`/matters/${matter_id}/coverage/${coverage_id}`);
          return;
        }
      }

      toast.success('Coverage updated successfully');
      navigate(`/matters/${matter_id}/coverage/${coverage_id}`);
    } catch (error) {
      toast.error('Failed to update coverage');
    }
  };

  // Check if required fields are filled for basic tab
  const isBasicInfoComplete = () => {
    const formData = watch();
    return !!(
      formData.category &&
      formData.policy_type &&
      formData.named_insured?.some((item) => item.name?.trim())
    );
  };

  const canSubmit = isBasicInfoComplete();

  const handleCancel = () => {
    navigate(`/matters/${matter_id}/coverage/${coverage_id}`);
  };

  // Smart tab navigation
  const handleNextTab = () => {
    const tabsArray: readonly string[] = availableTabs;
    const currentIndex = tabsArray.indexOf(activeTab);
    if (currentIndex < tabsArray.length - 1) {
      handleTabChange(tabsArray[currentIndex + 1] as CoverageTab);
    }
  };

  const handlePreviousTab = () => {
    const tabsArray: readonly string[] = availableTabs;
    const currentIndex = tabsArray.indexOf(activeTab);
    if (currentIndex > 0) {
      handleTabChange(tabsArray[currentIndex - 1] as CoverageTab);
    }
  };

  const isFirstTab = activeTab === availableTabs[0];
  const isLastTab = activeTab === availableTabs[availableTabs.length - 1];

  if (!matter_id || !coverage_id) {
    return <div>Invalid parameters</div>;
  }

  if (isLoadingCoverage || !isPrefilled) {
    return <div>Loading coverage...</div>;
  }

  if (!coverage) {
    return <div>Coverage not found</div>;
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Coverage
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-gray-900">
                  Edit Coverage
                </h1>
                {!canSubmit && (
                  <Badge
                    variant="outline"
                    className="text-amber-600 border-amber-300"
                  >
                    Incomplete
                  </Badge>
                )}
                {canSubmit && (
                  <Badge
                    variant="outline"
                    className="text-green-600 border-green-300"
                  >
                    Ready to Save
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Update the insurance coverage information for this matter
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Coverage ID:</span>
            <code className="bg-gray-100 px-2 py-1 rounded text-xs">
              {coverage_id}
            </code>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <form
            onSubmit={(e) => {
              benefitsFlushRef.current?.();
              handleSubmit(onSubmit)(e);
            }}
          >
            {/* Property Name section for premises, Coverage Name for others */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Pencil className="h-4 w-4 text-gray-500" />
                <Label className="text-base font-semibold text-gray-900">
                  {isPremisesCoverage ? 'Property Name' : 'Coverage Name'}
                </Label>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                {isPremisesCoverage
                  ? 'The name of the property where the incident occurred (shown as the title on the coverage card).'
                  : 'The carrier or insurer name shown as the title on the coverage card.'}
              </p>
              {isPremisesCoverage ? (
                <Input
                  placeholder="e.g., Lowes, Walmart Supercenter, 123 Main St"
                  value={propertyName}
                  onChange={(e) => {
                    hasUserEditedPropertyName.current = true;
                    setPropertyName(e.target.value);
                  }}
                  className="max-w-lg"
                />
              ) : (
                <Input
                  placeholder="e.g., State Farm, GEICO"
                  value={watch('carrier.name') || ''}
                  onChange={(e) => {
                    setValue('carrier.name', e.target.value, {
                      shouldDirty: true,
                    });
                  }}
                  className="max-w-lg"
                />
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <CoverageFormTabs
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  watch={watch}
                  isPremisesCoverage={isPremisesCoverage}
                  propertyContactsCount={
                    propertyContacts.filter((c) => c.name?.trim()).length
                  }
                />
                <div className="min-h-[500px]">
                  {activeTab === 'basic' && (
                    <BasicInfoTab
                      register={register}
                      setValue={setValue}
                      watch={watch}
                      insuredFields={insuredFields}
                      appendInsured={appendInsured}
                      removeInsured={removeInsured}
                      policyIdFields={policyIdFields}
                      appendPolicyId={appendPolicyId}
                      removePolicyId={removePolicyId}
                    />
                  )}

                  {activeTab === 'contacts' && isPremisesCoverage && (
                    <PropertyContactsTab
                      contacts={propertyContacts}
                      onChange={setPropertyContacts}
                      savedContacts={coverage?.property_contacts}
                    />
                  )}

                  {activeTab === 'basic' && (
                    <div className="mt-6">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        Preferred Contacts
                      </div>
                      <div className="flex items-center gap-3">
                        <CarrierPocketbook
                          trigger={
                            <Button variant="outline" size="sm">
                              Link a carrier contact
                            </Button>
                          }
                          onSelect={async ({ handle }) => {
                            try {
                              await addPreferred({
                                matterId: matterIdParam,
                                coverageId: coverageIdParam,
                                contactHandleId: Number(handle.id),
                              }).unwrap();
                              toast.success('Preferred contact linked');
                              refetchPreferred();
                            } catch {
                              toast.error('Failed to link preferred contact');
                            }
                          }}
                          actions={['use']}
                        />

                        {preferredCount ? (
                          <div className="text-xs text-gray-600">
                            Linked: {preferredCount}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">
                            No preferred contacts linked
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'policy' && (
                    <PolicyDetailsTab
                      register={register}
                      policyDetailFields={policyDetailFields}
                      appendPolicyDetail={appendPolicyDetail}
                      removePolicyDetail={removePolicyDetail}
                      vehicleFields={vehicleFields}
                      appendVehicle={appendVehicle}
                      removeVehicle={removeVehicle}
                    />
                  )}

                  {activeTab === 'benefits' && (
                    <BenefitsTab
                      setValue={setValue}
                      watch={watch}
                      updateBenefit={updateBenefit}
                      benefitFields={benefitFields}
                      appendBenefit={appendBenefit}
                      removeBenefit={removeBenefit}
                      flushRef={benefitsFlushRef}
                      coverageCategory={coverage?.category}
                      onMarkReviewComplete={
                        limitsReviewTask ? handleMarkReviewComplete : undefined
                      }
                      isMarkingComplete={isCompletingTask}
                      isReviewCompleted={isLimitsReviewTaskCompleted}
                    />
                  )}

                  {activeTab === 'claims' && (
                    <ClaimsInfoTab
                      register={register}
                      setValue={setValue}
                      watch={watch}
                      claimFields={claimFields}
                      appendClaim={appendClaim}
                      removeClaim={removeClaim}
                      onRemoveClaim={handleRemoveClaim}
                      removingClaimIndex={removingClaimIndex}
                      adjusterFields={adjusterFields}
                      appendAdjuster={appendAdjuster}
                      removeAdjuster={removeAdjuster}
                    />
                  )}
                </div>

                {/* Enhanced Actions */}
                <div className="flex items-center justify-between pt-6 mt-6 border-t bg-gray-50 -m-6 p-6 rounded-b-lg">
                  {/* Tab Navigation */}
                  <div className="flex gap-2">
                    {!isFirstTab && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousTab}
                        disabled={isUpdating}
                      >
                        Previous
                      </Button>
                    )}
                    {!isLastTab && isBasicInfoComplete() && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleNextTab}
                        disabled={isUpdating}
                      >
                        Next
                      </Button>
                    )}
                  </div>

                  {/* Primary Actions */}
                  <div className="flex items-center gap-3">
                    {!canSubmit && (
                      <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded border border-amber-200">
                        Complete the Basic Info tab to save changes
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUpdating || !canSubmit}
                      className="min-w-32"
                    >
                      {isUpdating ? 'Updating...' : 'Update Coverage'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
